import { Injectable } from '@angular/core';
import { AclService } from '@y42/acl';
import {
  AclAssetOrChildResourceType,
  AclAssetPermission,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclStrategy,
  AclResource,
  AclRole,
  AclRoleAssignment,
  AclSelectRolesContext,
  AclSubject,
} from '@y42/acl/models';
import { AclQuery } from '@y42/acl/state';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitQuery } from '@y42/git';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { isNullish } from '@y42/util/nullish';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AssetAclService {
  constructor(
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly aclQuery: AclQuery,
    private readonly aclService: AclService,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly gitQuery: GitQuery,
  ) {}

  enumerateAssets({
    resourceType,
    spaceId,
    permissions,
  }: {
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
    permissions: AclPermission[];
  }): Observable<AclResource[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    return this.aclService.enumerateResources({
      resourceType,
      spaceId,
      permissions,
      storeResources: true,
    });
  }

  getAssetRoles({ resourceType }: { resourceType: AclAssetOrChildResourceType }): AclRole[] {
    return this.aclQuery.getRoles({ resourceType });
  }

  convertAssetRoleToGrantPermission(role: AclRole): AclAssetPermission {
    return this.aclService.convertRoleToGrantPermission(role) as AclAssetPermission;
  }

  getAssetRoleTooltip({ role, resourceType }: { role: AclRole; resourceType: AclAssetOrChildResourceType }): string {
    return this.aclQuery.getRoleTooltip({ role, resourceType });
  }

  getAssetPermission({
    resourceType,
    assetPath,
    permission,
    spaceId,
  }: {
    resourceType: AclAssetOrChildResourceType;
    assetPath: string;
    permission: AclPermission;
    spaceId?: string;
  }): AclPermissionDetail | null {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.getAssetModulePermission]: nullish spaceId');
      return { allowed: false };
    }

    return this.aclQuery.getPermission({
      resourceType,
      resourceId: assetPath,
      spaceId,
      permission,
    });
  }

  checkAssetPermission({
    resourceType,
    assetPath,
    permission,
    spaceId,
    strategy,
  }: {
    resourceType: AclAssetOrChildResourceType;
    assetPath: string;
    permission: AclAssetPermission;
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclPermissionDetail> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetAclService.checkAssetPermission]: nullish spaceId');
      return of({ allowed: false });
    }

    const assetMetadataFile = `${assetPath}/metadata.json`;
    const isNewAsset = this.gitQuery.getUntrackedFiles()[assetMetadataFile];

    if (isNewAsset) {
      return of({
        allowed: true,
      });
    }

    return this.aclService.checkPermission({
      resourceType,
      resourceId: assetPath,
      permission,
      spaceId,
      strategy,
    });
  }

  checkAssetPermissions({
    resourceType,
    assetPath,
    permissions,
    spaceId,
    strategy,
  }: {
    resourceType: AclAssetOrChildResourceType;
    assetPath: string;
    permissions: AclPermission[];
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclPermissionsRecord> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetAclService.checkAssetPermissions]: nullish spaceId');
      return of({} as AclPermissionsRecord);
    }

    const assetMetadataFile = `${assetPath}/metadata.json`;
    const isNewAsset = this.gitQuery.getUntrackedFiles()[assetMetadataFile];

    if (isNewAsset) {
      return of(
        permissions.reduce((acc, permission) => {
          acc[permission] = {
            allowed: true,
          };
          return acc;
        }, {} as AclPermissionsRecord),
      );
    }

    return this.aclService.checkPermissions({
      resourceType,
      resourceId: assetPath,
      permissions,
      spaceId,
      strategy,
    });
  }

  filterAssetRolesBasedOnGrantedPermissions({
    roles,
    assetPath,
    resourceType,
    spaceId,
  }: {
    roles: AclRole[];
    assetPath: string;
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
  }): Observable<AclRole[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetAclService.filterAssetRolesBasedOnGrantedPermissions]: nullish spaceId');
      return of([]);
    }

    return this.aclService.filterRolesBasedOnGrantedPermissions({
      roles,
      resourceType,
      resourceId: assetPath,
      spaceId,
    });
  }

  setAssetRole({
    assetPath,
    resourceType,
    spaceId,
    role,
    subject,
  }: {
    assetPath: string;
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
    role: AclRole;
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetAclService.setAssetRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .setRole({
        resourceType,
        resourceId: assetPath,
        spaceId,
        subject,
        role,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[AssetAclService.setAssetRole]: could not set role for resource',
          () => of(false),
          {
            toast: `Could not set the role '${role}' for resource '${resourceType}'`,
            context: { role, assetPath, resourceType },
          },
        ),
      );
  }

  revokeAssetRole({
    assetPath,
    resourceType,
    spaceId,
    subject,
  }: {
    assetPath: string;
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetAclService.revokeAssetRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .revokeRole({
        resourceType,
        resourceId: assetPath,
        spaceId,
        subject,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[AssetAclService.revokeAssetRole]: could not revoke role for resource',
          () => of(false),
          {
            toast: `Could not revoke the role for resource '${resourceType}'`,
            context: { assetPath, resourceType },
          },
        ),
      );
  }

  getAssetRoleAssignments({
    assetPath,
    resourceType,
    spaceId,
    subject,
    strategy,
  }: {
    assetPath: string;
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclRoleAssignment[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error(`[AssetAclService.getAssetRoleAssignments]: nullish spaceId`);
      return of([]);
    }

    return this.aclService
      .getRoleAssignments({
        resourceType,
        resourceId: assetPath,
        spaceId,
        subject,
        strategy,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[AssetAclService.getAssetRoleAssignments]: could not fetch role assignments',
          () => of([]),
          {
            toast: `Could not fetch role assignments for resource "${assetPath.split('/').at(-1)}"`,
            context: { assetPath },
          },
        ),
      );
  }

  selectAssetRoleAssignmentsByPath({
    resourceType,
    assetPath,
  }: {
    resourceType: AclAssetOrChildResourceType;
    assetPath: string;
  }): Observable<AclRoleAssignment[]> {
    return this.aclQuery.selectRoleAssignments({ resourceType, resourceId: assetPath });
  }

  getAssetRolesContextForRoleSelect({
    assetPath,
    resourceType,
    spaceId,
    subject,
  }: {
    assetPath: string;
    resourceType: AclAssetOrChildResourceType;
    spaceId?: string;
    subject?: AclSubject;
  }): Observable<AclSelectRolesContext> {
    return this.aclService.getRolesContextForRoleSelect({
      resourceId: assetPath,
      resourceType,
      spaceId,
      subject,
    });
  }
}
